import { useAuth } from "./useAuth";
import { useCallback, useEffect } from "react";

/**
 * Hook to handle session expiration and redirection to login page
 *
 * This hook:
 * 1. Listens for 401 Unauthorized responses from API calls
 * 2. Detects token expiration events
 * 3. Redirects to login page when session expires
 * 4. Preserves the current URL for post-login redirect
 */
export const useSessionExpiration = () => {
    const { logout } = useAuth();

    /**
     * Handle session expiration by redirecting to login page
     */
    const handleSessionExpired = useCallback(() => {
        // Store current path for post-login redirect
        const currentPath = window.location.pathname;
        if (currentPath !== "/login") {
            sessionStorage.setItem("authRedirectPath", currentPath);
        }

        // Log out the user which will redirect to login
        logout();
    }, [logout]);

    /**
     * Listen for API error events that indicate session expiration
     */
    useEffect(() => {
        const handleApiError = (event: CustomEvent<{ status: number; message: string }>) => {
            const { status, message } = event.detail;

            // Check for authentication errors (401 Unauthorized)
            if (status === 401) {
                console.warn("Session expired or authentication error:", message);
                handleSessionExpired();
            }
        };

        // Create custom event listener for API errors
        window.addEventListener("api-error", handleApiError as EventListener);

        return () => {
            window.removeEventListener("api-error", handleApiError as EventListener);
        };
    }, [handleSessionExpired]);

    /**
     * Listen for MSAL token expiration events
     */
    useEffect(() => {
        const handleMsalError = (event: Event) => {
            const msalEvent = event as CustomEvent<{ error?: { errorCode: string } }>;

            if (
                msalEvent.detail?.error?.errorCode === "interaction_required" ||
                msalEvent.detail?.error?.errorCode === "token_renewal_error"
            ) {
                console.warn("MSAL token expired or requires interaction");
                handleSessionExpired();
            }
        };

        // Listen for MSAL errors
        window.addEventListener("msal:error", handleMsalError);

        return () => {
            window.removeEventListener("msal:error", handleMsalError);
        };
    }, [handleSessionExpired]);

    return {
        handleSessionExpired,
    };
};
